import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { DetailsLayout } from "../components/bifrost"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Grid, Box, Tooltip, Button, FormControl, InputLabel, Select, MenuItem, Chip, Typography } from "@material-ui/core"
import LabelValue from "../components/bifrost/common/LabelValue"
import { getPiiDetailsRequest } from "../actions/bifrostActions"
import { getFormattedTime, getStatusColor } from "../utils/helperFunctions"
import TableView from "../components/common/TableView/TableView"
import AddIcon from "@material-ui/icons/Add"
import AiiNamesDialog from "../components/common/dialog/Simple"
import { getFileFieldValues } from "../services/bifrostServices"
import { useTranslation } from "react-i18next"
import WithDrawer from "../components/common/drawer/withDrawer"
const useStyles = makeStyles((theme) => ({
	jobDetails: {
		"& .MuiChip-root": {
			borderRadius: "4px",
		},
		"& .MuiDataGrid-cell": {
			padding: "0 16px",
		},
	},
	actionDiv: {
		display: "flex",
		justifyContent: "space-between",
	},
	primaryBtn: {
		textTransform: "initial",
		fontSize: theme.typography.fontSize,
		padding: "8px 12px",
		color: theme.palette.primary.dark,
	},
	formControl: {
		margin: theme.spacing(1),
		width: 140,
		"& .MuiSelect-icon": {
			color: theme.palette.common.black,
		},
		"& .MuiSelect-outlined": {
			backgroundColor: theme.palette.grey[100],
			borderRadius: "5px",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "transparent",
		},
	},
	aiiNamesDialogBox: {
		maxHeight: "250px",
		"& p": {
			paddingBottom: 5,
			...theme.typography.body2,
		},
	},
}))

const PagePIIExtractDetails = () => {
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const classes = useStyles()
	const history = useHistory()
	const { caseId, jobId } = useParams()
	const theme = useTheme()

	const piiDetails = useSelector((state) => state.bifrostPiiDetails.piiDetails)
	const isPageLoading = useSelector((state) => state.bifrostPiiDetails.isLoading)

	const [pageNumber, setPageNumber] = React.useState(1)
	const [pageSize, setPageSize] = React.useState(10)
	const [fileStatus, setFileStatus] = React.useState("")
	const [fileType, setFileType] = React.useState("")
	const [fileStatusList, setFileStatusList] = React.useState([])
	const [fileTypesList, setFileTypeList] = React.useState([])
	const [aiiNamesDialogOpen, setAiiNamesDialogOpen] = React.useState(false)
	const [aiiNamesDailogData, setAiiNamesDailogData] = useState([])

	const MenuProps = {
		PaperProps: {
			style: {
				maxWidth: 216,
				maxHeight: 300,
			},
		},
		anchorOrigin: {
			vertical: "bottom",
			horizontal: "left",
		},
		getContentAnchorEl: null,
	}

	useEffect(() => {
		getPiiExtractDetails()
		getFileStatusList()
		getFileTypeList()
	}, [])

	useEffect(() => {
		getPiiExtractDetails()
	}, [pageNumber, pageSize])

	useEffect(() => {
		getPiiExtractDetails(true)
	}, [fileStatus, fileType])

	const getPiiExtractDetails = (resetPage) => {
		const payload = {
			jobId: jobId,
			filters: {
				file_status: fileStatus,
				file_type: fileType,
			},
			page_number: resetPage ? 1 : pageNumber,
			page_size: pageSize,
		}
		dispatch(getPiiDetailsRequest(payload))
	}

	const getFileStatusList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "jobs.file_status")
		setFileStatusList(result.data.unique_values)
	}

	const getFileTypeList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "original_file_type")
		setFileTypeList(result.data.unique_values)
	}

	const getActionItems = () => {
		return (
			<div className={classes.actionDiv}>
				<Button
					onClick={() => {
						history.push(`/bifrost/case/${caseId}`)
					}}
					className={classes.primaryBtn}
				>
					{t("bifrost.buttonTexts.back")}
				</Button>
			</div>
		)
	}

	const getFileActions = (file_status) => {
		let actionValue = "NA"
		switch (file_status) {
			case "in process":
			case "waiting":
			default:
				actionValue = (
					<Button onClick={() => {}} className={classes.primaryBtn}>
						{t("bifrost.buttonTexts.skip")}
					</Button>
				)
				break
			case "succeeded":
				actionValue = (
					<Button onClick={() => {}} style={{ paddingLeft: "15px" }} className={classes.primaryBtn}>
						{t("bifrost.buttonTexts.rollback")}
					</Button>
				)
				break
			case "failed":
				actionValue = (
					<>
						<Button onClick={() => {}} className={classes.primaryBtn}>
							{t("bifrost.buttonTexts.skip")}
						</Button>{" "}
						|
						<Button onClick={() => {}} color="primary" className={classes.primaryBtn}>
							{t("bifrost.buttonTexts.retry")}
						</Button>
					</>
				)
				break
		}
		return <div>{actionValue}</div>
	}

	const getRows = () => {
		return piiDetails?.files
			? piiDetails.files.map((file) => ({
					id: file.file_id,
					name: file.name,
					original_file_type: file.original_file_type,
					size: file.human_readable_size || "NA",
					path: file.original_file?.path,
					file_status: file.jobs[0]?.file_status,
					ai_names: file.metadata?.pii_names,
			  }))
			: []
	}

	const getToolTipSection = (value) => {
		return (
			<Tooltip title={value} placement="top">
				<Box style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{value}</Box>
			</Tooltip>
		)
	}

	const renderActionHeader = () => {
		return <Box style={{ paddingLeft: "16px", fontWeight: "500" }}>{t("bifrost.table_headers.action")}</Box>
	}

	const getColumns = () => {
		return [
			{
				field: "name",
				headerName: t("bifrost.table_headers.fileName"),
				sortable: false,
				resizable: true,
				width: 260,
				renderCell: (params) => getToolTipSection(params?.row?.name),
			},
			{
				field: "original_file_type",
				headerName: t("bifrost.table_headers.fileType"),
				sortable: false,
				width: 150,
			},
			{
				field: "size",
				headerName: t("bifrost.table_headers.fileSize"),
				sortable: false,
				width: 150,
			},
			{
				field: "path",
				headerName: t("bifrost.table_headers.filePath"),
				sortable: false,
				width: 320,
				renderCell: (params) => getToolTipSection(params?.row?.path),
			},
			{
				field: "file_status",
				headerName: t("bifrost.table_headers.fileStatus"),
				sortable: false,
				width: 180,
				renderCell: (params) => (
					<Chip
						label={params?.row?.file_status}
						size="small"
						style={{
							color: getStatusColor(params?.row?.file_status, theme).color,
							backgroundColor: getStatusColor(params?.row?.file_status, theme).background,
						}}
					/>
				),
			},
			{
				field: "ai_names",
				headerName: t("bifrost.table_headers.aiNamesFromFile"),
				sortable: false,
				width: 250,
				renderCell: (params) => renderAiiNames(params.row.ai_names),
			},
			{
				field: "action",
				headerName: t("bifrost.table_headers.action"),
				width: 180,
				sortable: false,
				renderCell: (params) => getFileActions(params?.row?.file_status),
				renderHeader: () => renderActionHeader(),
			},
		]
	}

	const renderAiiNames = (aiNames) => {
		return (
			<Box
				id="abc"
				variant="body2"
				style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
			>
				{!!aiNames?.length ? (
					<>
						<div style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", paddingRight: "8px" }}>
							{`${aiNames[0]}`}
						</div>
						{aiNames?.length > 1 && (
							<Chip
								icon={<AddIcon color={"action"} />}
								label={aiNames.length - 1}
								size="small"
								style={{
									color: theme.palette.grey[700],
									backgroundColor: theme.palette.status.count_background,
									fontWeight: "500",
								}}
								onClick={() => {
									setAiiNamesDailogData(aiNames)
									setAiiNamesDialogOpen(true)
								}}
							/>
						)}
					</>
				) : (
					"—"
				)}
			</Box>
		)
	}

	const handleFileStatusChange = (event) => {
		setFileStatus(event.target.value)
	}
	const handleFileTypeChange = (event) => {
		setFileType(event.target.value)
	}

	const handlePageNumberChange = (page) => {
		setPageNumber(page)
	}
	const handlePageSizeChange = (pageSize) => {
		setPageSize(pageSize)
	}

	const getFileStatusFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file_status-select-label">{t("bifrost.filters.fileStatus")}</InputLabel>
					<Tooltip placement="top" arrow title={fileStatus}>
						<Select
							labelId="file-status-select-label"
							id="file-status-select"
							value={fileStatus}
							label={t("bifrost.filters.fileStatus")}
							onChange={handleFileStatusChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_status.all")}</em>
							</MenuItem>
							{fileStatusList.map((fileStatus) => {
								return (
									<MenuItem key={fileStatus} value={fileStatus}>
										{fileStatus}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getFileTypeFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file-type-select-label">{t("bifrost.filters.fileType")}</InputLabel>
					<Tooltip placement="top" arrow title={fileType}>
						<Select
							labelId="file-type-select-label"
							id="file-type-select"
							value={fileType}
							label={t("bifrost.filters.fileType")}
							onChange={handleFileTypeChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_types.all")}</em>
							</MenuItem>
							{fileTypesList.map((fileType) => {
								return (
									<MenuItem key={fileType} value={fileType}>
										{fileType}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getListView = () => {
		return (
			<TableView
				columns={getColumns()}
				rows={getRows()}
				isLoading={isPageLoading}
				pageInfo={{
					page: pageNumber,
					pageSize: pageSize,
					rowsPerPageOptions: [5, 10, 20, 50],
					totalRowCount: piiDetails?.pagination?.total_count,
					paginationMode: "server",
					handlePageChange: handlePageNumberChange,
					handlePageSizeChange: handlePageSizeChange,
				}}
				sortInfo={{
					sortingMode: "server",
					sortModel: [],
					onSortModelChange: () => {},
				}}
				checkboxSelection={false}
				footerInfo={{
					hideFooter: false,
					hideSelectionCount: true,
					hidePagination: false,
				}}
				topBarInfo={{
					show: true,
					leftSection: "All files",
					rightSection: (
						<>
							{getFileTypeFilter()} {getFileStatusFilter()}
						</>
					),
				}}
				totalCountText={`${t("tableColumns.totalFiles")}:  ${piiDetails?.pagination?.total_count || 0}`}
				showDownloadButton={false}
				showColumnsButton={false}
			/>
		)
	}

	return (
		<div data-cy="job-details" className={classes.jobDetails}>
			<DetailsLayout
				loading={isPageLoading}
				titleProps={{
					title: t("bifrost.titles.jobDetails"),
					name: piiDetails?.name,
					status: piiDetails?.status,
				}}
				actionItems={getActionItems()}
				listView={getListView()}
				showActionsView={true}
			>
				<>
					<Grid item xs={9}>
						<LabelValue label={t("bifrost.metadataKeys.caseName")} value={piiDetails?.case_name} loading={isPageLoading} />
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggerDate")}
									value={!!piiDetails?.create_time ? new Date(piiDetails?.create_time).toLocaleString() : "—"}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={5}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalTime")}
									value={
										piiDetails?.total_time === 0
											? t("bifrost.metadataKeys.calculating")
											: getFormattedTime(piiDetails?.total_time)
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={9}>
						<Grid container item direction="row" xs={9}>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalFileCount")}
									value={piiDetails?.execution_status?.total || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue label={t("bifrost.metadataKeys.totalFileSize")} value={"—"} loading={isPageLoading} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggeredBy")}
									value={piiDetails.created_by_username}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Grid item xs={9}>
							<Grid container item direction="row" xs={9}>
								<Grid item xs={3}>
									<LabelValue
										label={t("bifrost.metadataKeys.completedFiles")}
										value={piiDetails?.execution_status?.summary?.Done || 0}
										loading={isPageLoading}
									/>
								</Grid>
								<Grid item xs={3}>
									<LabelValue
										label={t("bifrost.metadataKeys.failedFiles")}
										value={piiDetails?.execution_status?.summary?.Failed || 0}
										loading={isPageLoading}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</>
			</DetailsLayout>
			<AiiNamesDialog
				open={aiiNamesDialogOpen}
				title={t("bifrost.table_headers.aiNamesFromFile")}
				onClose={() => {
					setAiiNamesDialogOpen(false)
				}}
			>
				<Box className={classes.aiiNamesDialogBox}>
					{aiiNamesDailogData?.map((aiiName, index) => {
						return <Typography key={`aii-name-${index}`}>{aiiName}</Typography>
					})}
				</Box>
			</AiiNamesDialog>
		</div>
	)
}

export default WithDrawer(PagePIIExtractDetails)
